import {
  AlertDialogPortalHost,
  MODAL_SURFACE_ALERT_DIALOG_PORTAL_HOST_NAME,
} from "@-/dev.improve.haniverse.components.ui.alert-dialog";
import { Link, Slot, useNavigation, usePathname } from "@-/com.npmjs.expo-router";
import {
  BottomSheet,
  type BottomSheetHandle,
} from "@-/dev.improve.haniverse.components.ui.bottom-sheet";
import { Modal, Platform, Pressable, View } from "@-/com.npmjs.react-native";
import { GestureHandlerRootView } from "@-/com.npmjs.react-native-gesture-handler";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import { useEffect, useRef } from "react";
import { BottomSheetModalProvider } from "@-/dev.improve.haniverse.components.ui.bottom-sheet-provider";

const SUPPORT_TABS = [
  { href: "/support", label: "Support" },
  { href: "/support/about", label: "About" },
  { href: "/support/terms", label: "Terms" },
  { href: "/support/privacy", label: "Privacy" },
] as const;

export function SupportSheetLayout() {
  const navigation = useNavigation("/(nav)");
  // Android's transparent native-stack screen is still clipped and translated by
  // the surrounding drawer. A native dialog window puts Help above that surface
  // without closing the drawer underneath it. The dialog needs its own gesture root.
  if (Platform.OS === "android") {
    return (
      <Modal
        hardwareAccelerated
        navigationBarTranslucent
        onRequestClose={() => navigation.goBack()}
        statusBarTranslucent
        transparent
        visible
      >
        <GestureHandlerRootView className="flex-1">
          <BottomSheetModalProvider>
            <SupportSheetContent />
          </BottomSheetModalProvider>
        </GestureHandlerRootView>
      </Modal>
    );
  }
  // Own the portal inside the modal screen so native hit testing and accessibility
  // see the sheet in the foreground, rather than behind the transparent screen.
  return (
    <BottomSheetModalProvider>
      <SupportSheetContent />
    </BottomSheetModalProvider>
  );
}

function SupportSheetContent() {
  const sheet = useRef<BottomSheetHandle>(null);
  const mounted = useRef(false);
  const navigation = useNavigation("/(nav)");
  const pathname = usePathname();

  useEffect(() => {
    mounted.current = true;
    sheet.current?.open();
    return () => {
      mounted.current = false;
    };
  }, []);

  return (
    <BottomSheet
      // Each nested route owns a native ScrollView. Keep vertical content gestures with it;
      // the sheet's handle still owns dragging and dismissal.
      enableContentPanningGesture={false}
      fullHeight
      onDismiss={() => {
        if (!mounted.current) {
          return;
        }
        // Pop the support route, including its entire nested history.
        if (navigation.canGoBack()) {
          navigation.goBack();
        } else {
          navigation.dispatch({
            payload: { index: 0, routes: [{ name: "(app)" }] },
            type: "RESET",
          });
        }
      }}
      ref={sheet}
    >
      <View accessibilityViewIsModal className="flex-1 gap-4" testID="help-support-sheet">
        {/*
          Alert dialogs opened from inside this sheet must mount HERE, not at the app root. This
          View is `accessibilityViewIsModal`, so anything portalled beside it is dropped from
          Android's accessibility tree — visible on screen, unreachable by TalkBack (and by Maestro:
          `onboarding.yaml` timed out on the "Reset onboarding?" confirm in run 35744282332).
          Hosting them inside the modal subtree keeps them in the tree the modal exposes.
        */}
        <AlertDialogPortalHost name={MODAL_SURFACE_ALERT_DIALOG_PORTAL_HOST_NAME} />
        <View className="flex-row items-center justify-between">
          <Text className="text-xl font-semibold">Help and support</Text>
          <Pressable
            accessibilityLabel="Close support"
            accessibilityRole="button"
            className="p-2"
            onPress={() => sheet.current?.close()}
          >
            <Text>Close</Text>
          </Pressable>
        </View>
        <View accessibilityRole="tablist" className="flex-row gap-2">
          {SUPPORT_TABS.map(({ href, label }) => (
            <Link asChild href={href} key={href}>
              <Pressable
                accessibilityRole="tab"
                accessibilityState={{ selected: pathname === href }}
                className={
                  pathname === href ? "rounded-full bg-muted px-3 py-2" : "rounded-full px-3 py-2"
                }
              >
                <Text>{label}</Text>
              </Pressable>
            </Link>
          ))}
        </View>
        <View className="min-h-0 flex-1">
          <Slot />
        </View>
      </View>
    </BottomSheet>
  );
}
